import { useEffect, useState, useCallback } from "react";
import { getProductVariant, getRecursiveChildFields, getRecursiveParentFields, sortFunction } from "../../../../../utilities";
import { COMMON_ACCESS_ACTION } from "../../../../../constants";
import { addSelectedGeographyTags, fetchSelectedGeographyTags, fetchGeographyTagsMaster } from "middleware/services/companyApi";
import { CheckBoxSelection, Inject, MultiSelectComponent } from "@syncfusion/ej2-react-dropdowns";
import SortableTree from "../../../../components/treeView/sortableTree";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { actionError, actionSuccess, hideLoader, showLoader } from "middleware/actions/utilityAction";
import { useDispatch } from "react-redux";
import { showAlertBox } from "middleware/actions/alertBoxAction";
import { SUCCESS_GEO } from "../../constants";
import noTaxonomyImage from "../../../../../assets/images/no-taxonomy.svg";
import "./index.scss";

const GeographyTagging = (props) => {
  const companyId = props.companyId;
  if (companyId == "") {
    return null;
  }
  const productVariant = getProductVariant();
  if (!productVariant || productVariant == "") return null;
  const cancelPath = props.path === "/processing/company-old/:companyId/edit" ? "/processing/company-old" : "/company-old";

  let deleteAccess = props?.interfaceActionAccess.includes(COMMON_ACCESS_ACTION.DELETE);
  let addAccess = props?.interfaceActionAccess.includes(COMMON_ACCESS_ACTION.ADD);
  const history = useHistory();

  let dispatch = useDispatch();

  const [geographyTagsList, setGeographyTagsList] = useState([]);
  const [region, setRegion] = useState([]);
  const [regionList, setRegionList] = useState([]);
  const [reRender, setReRender] = useState(false);

  const [leftTreeData, setLeftTreeData] = useState([]);
  const [leftTreeCollapse, setLeftTreeCollapse] = useState(true);
  const [leftTreeChecked, setLeftTreeChecked] = useState([]);

  const [rightTreeCollapse, setRightTreeCollapse] = useState(true);
  const [rightTreeChecked, setRightTreeChecked] = useState([]);
  const [rightTreeData, setRightTreeData] = useState([]);
  const [selectedTagsList, setSelectedTagsList] = useState([]);
  const buildTreeJson = (data, parentId) => {
    const result = [];
    data.forEach((element) => {
      element["value"] = element.id;
      element["label"] = element.taxonomyName;
      element["level"] = element.taxonomyLevel;
      if (element["taxonomyParentId"] == parentId) {
        const children = buildTreeJson(data, element["id"]);
        if (children.length > 0) {
          element["children"] = children;
        }
        result.push(element);
      }
    });
    return result;
  };

  /*
         @Description : Build tree data for tagged values
         @param : 
         data : taxonomy data
         parentId :industry id
     */
  const buildSelectedTreeJson = (arr) => {
    // Create a map of the items by their IDs
    const map = new Map();

    // First pass: populate the map with nodes, keyed by their 'taxonomyId'
    arr.forEach((item) => {
      map.set(item.id, { ...item, label: item.taxonomyName, children: [] });
    });

    // Second pass: link each item to its parent (if any)
    const tree = [];
    arr.forEach((item) => {
      if (item.taxonomyParentId && map.has(item.taxonomyParentId)) {
        const parent = map.get(item.taxonomyParentId);

        if (parent) {
          parent.children.push(map.get(item.id));
        }
      } else {
        // If no parentId, this is a root node
        tree.push(map.get(item.id));
      }
    });

    return tree;
  };

  const getGeographyData = async () => {
    try {
      dispatch(showLoader());
      let res = await fetchGeographyTagsMaster();
      res.sort((a, b) => sortFunction(a.taxonomyName, b.taxonomyName));
      let newRes = res.map((item) => ({ ...item, level: item?.taxonomyLevel ?? null }));
      setGeographyTagsList(newRes);
      let regions = newRes.filter((item) => item.taxonomyLevel == 3);

      setRegionList(regions);
      let initialSelectedRegions = regions.map((item) => item?.id);
      setRegion(initialSelectedRegions);
      const geographyTreeData = buildSelectedTreeJson(newRes);
      setLeftTreeData(geographyTreeData);
      dispatch(hideLoader());
    } catch (e) {
      dispatch(hideLoader());
      dispatch(actionError("Something Went Wrong...!"));
      console.log("err ", e);
    }
  };

  const handleRegionChange = (event) => {
    try {
      setRegion(event.value);
      return;
    } catch (err) {
      console.log(" err ", err);
    }
  };

  const createLeftTreeData = (selectedTags = geographyTagsList) => {
    let filteredRegions = selectedTags.filter(
      (item) => region.includes(item?.id ?? item?.taxonomyId) || item?.taxonomyName == "Global" || region.includes(item?.taxonomyParentId)
    );
    const geographyTreeData = buildSelectedTreeJson(filteredRegions);
    return geographyTreeData;
  };

  const flatRightTreeData = useCallback(() => {
    let flatData = [];
    rightTreeData.map((item) => {
      if (item?.children) {
        flatData.push(...item?.children);
        item.children.map((itemInner) => {
          if (itemInner?.children) {
            flatData.push(...itemInner?.children);
            itemInner.children?.map((level3Item) => {
              if (level3Item?.children) {
                flatData.push(...level3Item?.children);
              }
            });
          }
        });
      }
      flatData.push(item);
    });
    return flatData;
  }, [rightTreeChecked, rightTreeData]);

  const flatLeftTreeData = useCallback(() => {
    let flatData = [];
    leftTreeData.map((item) => {
      if (item?.children) {
        flatData.push(...item?.children);
        item.children.map((itemInner) => {
          if (itemInner?.children) {
            flatData.push(...itemInner?.children);
            itemInner.children?.map((level3Item) => {
              if (level3Item?.children) {
                flatData.push(...level3Item?.children);
              }
            });
          }
        });
      }
      flatData.push(item);
    });
    return flatData;
  }, [leftTreeChecked, leftTreeData]);

  const getSelectedLeftNodes = useCallback(() => {
    let selectedNodes = geographyTagsList.filter((item) => leftTreeChecked.includes(item?.id));
    return selectedNodes;
  }, [leftTreeChecked, geographyTagsList]);

  const getSelectedRightNodes = useCallback(() => {
    let selectedNodes = (flatRightTreeData() ?? []).filter((item) => rightTreeChecked.includes(item?.id));
    return selectedNodes;
  }, [rightTreeChecked, flatRightTreeData]);

  const getSelectedTags = useCallback(
    async (selectedTags = [], force = false) => {
      let res = selectedTags.length == 0 && force ? await fetchSelectedGeographyTags(companyId) : selectedTags;
      let selectedTagsDataWithoutLevel = selectedTags.length == 0 ? res?.data?.data ?? [] : selectedTags;

      let selectedTagsData = selectedTagsDataWithoutLevel.map((item) => ({ ...item, level: item?.taxonomyLevel ?? null }));

      if (!selectedTagsData && force != true) {
        setLeftTreeChecked([]);
        let leftTreeData = createLeftTreeData(geographyTagsList.map((item) => ({ ...item, isChecked: false })));
        setLeftTreeData(leftTreeData);
        setSelectedTagsList([]);
        setRightTreeData([]);
        setRightTreeChecked([]);
        return;
      }

      let tempRes = [...selectedTagsData].map((x) => {
        let taxonomyId = x.id || x.taxonomyId;
        let newX = { ...x, id: taxonomyId, taxonomyId, value: x.label };
        return newX;
      });

      let selectedTempResId = tempRes.map((item) => item?.id ?? item?.taxonomyId);
      setLeftTreeChecked(selectedTempResId);
      let updatedCheckedLeftTree = geographyTagsList.map((item) => {
        return { ...item, isChecked: selectedTempResId.includes(item?.id ?? item?.taxonomyId) ? true : false };
      });

      let updatedCheckedLeftTreeData = createLeftTreeData(updatedCheckedLeftTree);
      setLeftTreeData(updatedCheckedLeftTreeData);
      setSelectedTagsList(tempRes.map((item) => ({ ...item, isChecked: true })));
      let rightTreeDataWithoutChecked = tempRes.map((item) => ({ ...item, isChecked: false }));
      const selectedTreeMap = [...new Map(rightTreeDataWithoutChecked.map((v) => [v.id, v])).values()];

      const selectedTreeData = buildSelectedTreeJson(selectedTreeMap);

      setRightTreeData(selectedTreeData);
    },
    [leftTreeChecked, rightTreeChecked, leftTreeData, rightTreeData, selectedTagsList, rightTreeData?.length, leftTreeData?.length]
  );

  useEffect(() => {
    getSelectedTags([], true);
    getGeographyData();
  }, []);

  useEffect(() => {
    let newGeographyTagsList = [];
    if (selectedTagsList?.length > 0 && geographyTagsList?.length > 0) {
      let checkedNodes = [];
      let selectedTagsId = selectedTagsList.map((item) => item.id);
      newGeographyTagsList = geographyTagsList.map((item) => {
        if (selectedTagsId.includes(item.id)) {
          checkedNodes.push(item.id);
          item["isChecked"] = true;
        } else {
          item["isChecked"] = false;
        }
        return item;
      });
      setLeftTreeChecked(checkedNodes);
    } else {
      newGeographyTagsList = geographyTagsList.map((item) => {
        item["isChecked"] = false;
        return item;
      });
      setLeftTreeChecked([]);
    }
    let newLeftTreeData = createLeftTreeData(newGeographyTagsList);
    setLeftTreeData(newLeftTreeData);
  }, [selectedTagsList, geographyTagsList, rightTreeData]);

  const handleAdd = useCallback(() => {
    try {
      let prevSelectedNodes = flatRightTreeData();
      let selectedNodes = [...new Set([...(getSelectedLeftNodes() ?? []), ...prevSelectedNodes])];
      let selectedNodesId = selectedNodes.map((item) => item?.id);

      let nodesWithParent = [];
      selectedNodesId.forEach((tID) => {
        nodesWithParent = [...nodesWithParent, ...getRecursiveParentFields(tID, "taxonomyParentId", "id", geographyTagsList, true)];
      });
      getSelectedTags(nodesWithParent);
      setTimeout(() => {
        setRightTreeCollapse(true);
        setLeftTreeCollapse(true);
        setRightTreeChecked([]);
        dispatch(hideLoader());
      }, 600);
    } catch (err) {
      dispatch(hideLoader());
      dispatch(actionError("Something Went Wrong...!"));
    }
  }, [leftTreeChecked]);

  const handleDelete = () => {
    try {
      let selectedNodes = getSelectedRightNodes();
      let selectedNodesId = selectedNodes.map((item) => item?.id);
      let nodesToBeRemoved = [];
      selectedNodesId.forEach((tID) => {
        nodesToBeRemoved = [...nodesToBeRemoved, ...getRecursiveChildFields(tID, "id", "taxonomyParentId", geographyTagsList, true)];
      });

      let removedNodesId = nodesToBeRemoved.filter((x) => x).map((x) => x.id);
      let allRightNodes = flatRightTreeData().map((x) => x.id);

      let remainingNodesId = allRightNodes.filter((item) => !removedNodesId.includes(item));
      let remainingObjects = geographyTagsList.filter((item) => remainingNodesId.includes(item.id));

      setSelectedTagsList(remainingObjects);

      getSelectedTags(
        remainingObjects.filter((x) => x),
        false
      );
      setTimeout(() => {
        setRightTreeCollapse(true);
        setLeftTreeCollapse(true);
        setRightTreeChecked([]);
        dispatch(hideLoader());
      }, 600);
    } catch (err) {
      dispatch(hideLoader());
      console.log("handleDelete err ", err);
    }
  };

  const handleDeleteDialog = () => {
    if (deleteAccess)
      dispatch(
        showAlertBox({
          okCallback: async () => {
            dispatch(showLoader());
            handleDelete();
          },
          okText: "Delete",
          cancelText: "Cancel",
          content: "Do you want to remove Geography tagging ? ",
          title: "dialogAlertCss",
        })
      );
  };

  useEffect(() => {
    let regionId = region;

    let filteredGeographyListtemp = geographyTagsList?.filter((item) => {
      return regionId.includes(item?.id ?? item?.taxonomyId) || item?.taxonomyName == "Global" || region.includes(item?.taxonomyParentId);
    });
    let newTreeDatatemp = createLeftTreeData(filteredGeographyListtemp);
    setLeftTreeData(newTreeDatatemp);
    if (newTreeDatatemp) {
    } else {
      setLeftTreeData([]);
    }
    setLeftTreeCollapse((prev) => !prev);
    setTimeout(() => {
      setLeftTreeCollapse((prev) => !prev);
    }, 200);
  }, [region]);

  return (
    <>
      <div
        style={{
          margin: "1rem 1rem 1rem 1rem",
          height: "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            width: "100%",
            justifyContent: "space-between",
            backgroundColor: "#FAFAFA",
            minHeight: "100%",
            flexGrow: 1,
          }}
        >
          {/* left side */}
          <div
            style={{
              backgroundColor: "",
              width: "50%",
              border: "0px",
              borderRightWidth: "1px",
              borderStyle: "solid",
              borderColor: "#DBDBDD",
              padding: "1rem",
              height: "100%",
              minHeight: "900px",
              flexGrow: 1,
              paddingRight: "0px",
            }}
          >
            <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
              <div>
                <div style={{ width: "220px", marginBottom: "0.7rem" }} className="region-ddl-company">
                  {regionList?.length && regionList?.length > 0 ? (
                    <MultiSelectComponent
                      autoComplete="off"
                      name="test"
                      mode="CheckBox"
                      sortOrder="Acending"
                      showSelectAll={true}
                      selectAllText="Select All"
                      unSelectAllText="Select All"
                      showClearButton={false}
                      enableRtl={true}
                      required="required"
                      placeholder="Select Region"
                      value={region}
                      allowCustomValue={false}
                      key={reRender}
                      change={handleRegionChange}
                      cssClass={"customCss e-outline region-ddl-company-comp"}
                      floatLabelType="Auto"
                      popupHeight="250px"
                      dataSource={regionList}
                      fields={{ text: "taxonomyName", value: "id" }}
                    >
                      <Inject services={[CheckBoxSelection]} />
                    </MultiSelectComponent>
                  ) : null}
                </div>
              </div>
              <div>
                <div className="float-right">
                  <button
                    type="button"
                    className={`btn  px-3  mr-3 ${leftTreeChecked.length > 0 && addAccess ? "btn-primary" : "btn-primary-disabled"}`}
                    onClick={() => {
                      if (addAccess) {
                        dispatch(showLoader());
                        handleAdd();
                      }
                    }}
                  >
                    Add
                  </button>
                  {leftTreeCollapse ? (
                    <span
                      className="expandButton btn px-3 mr-3"
                      style={{
                        border: "1px solid #F94F5E",
                        color: "#F94F5E",
                      }}
                      onClick={() => setLeftTreeCollapse(false)}
                    >
                      Expand All{" "}
                    </span>
                  ) : (
                    <span
                      className="expandButton btn px-2.5 mr-3"
                      style={{
                        border: "1px solid #F94F5E",
                        color: "#F94F5E",
                      }}
                      onClick={() => setLeftTreeCollapse(true)}
                    >
                      Collapse All
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div key={region}>
              <SortableTree
                key={"leftTreeData"}
                treeData={leftTreeData.sort((a, b) => sortFunction(a.taxonomyName, b.taxonomyName))}
                onClick={(test) => {
                  setLeftTreeChecked(test?.map((x) => x?.id));
                }}
                treeCollapse={leftTreeCollapse}
                parentId={"taxonomyParentId"}
                flatData={true}
                hieghtClass={"tagging-tree-container-wrapper"}
                icon={true}
                checked={leftTreeChecked}
                newSearchbox={true}
                enablePersistance={true}
              />
            </div>
          </div>
          {/* right side */}
          <div
            style={{
              backgroundColor: "",
              width: "50%",
              padding: "1rem",
              minHeight: "100%",
              paddingRight: "0px",
            }}
          >
            <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
              <div style={{ marginBottom: "2rem" }}>
                <h6 className="font-weight-bold">Selected Geography</h6>
              </div>
              <div>
                <div className="float-right">
                  <button
                    type="button"
                    className={`btn  px-3  mr-3 ${rightTreeChecked.length > 0 && deleteAccess ? "btn-primary" : "btn-primary-disabled"}`}
                    onClick={() => {
                      if (deleteAccess) {
                        handleDeleteDialog();
                      }
                    }}
                  >
                    Delete
                  </button>
                  {rightTreeCollapse ? (
                    <span
                      className="expandButton btn px-3 mr-3"
                      style={{
                        border: "1px solid #F94F5E",
                        color: "#F94F5E",
                      }}
                      onClick={() => setRightTreeCollapse(false)}
                    >
                      Expand All{" "}
                    </span>
                  ) : (
                    <span
                      className="expandButton btn px-3 mr-3"
                      style={{
                        border: "1px solid #F94F5E",
                        color: "#F94F5E",
                      }}
                      onClick={() => setRightTreeCollapse(true)}
                    >
                      Collapse All
                    </span>
                  )}
                </div>
              </div>
            </div>
            {rightTreeData && rightTreeData?.length > 0 ? (
              <SortableTree
                key={"rightTreeData"}
                treeData={rightTreeData.sort((a, b) => sortFunction(a.taxonomyName, b.taxonomyName))}
                onClick={(test) => {
                  setRightTreeChecked(test?.map((x) => x?.id));
                }}
                treeCollapse={rightTreeCollapse}
                parentId={"taxonomyParentId"}
                flatData={true}
                hieghtClass={"tagging-tree-container-wrapper"}
                icon={true}
                newSearchbox={true}
                enablePersistance={true}
                checked={rightTreeChecked}
              />
            ) : (
              <div className="text-center" style={{ minHeight: "60%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div>
                  <img src={noTaxonomyImage} alt="No Tagging" width="200" height="200" />
                  <div className="info-container">
                    <p>Please select geography tag(s)</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="form-submit-box">
        <div className="footer-layout">
          {props.selectedIndex > 0 || props.selectedIndex === undefined ? (
            <button
              type="button"
              className="btn btn-outline-dark"
              onClick={() => {
                (props.selectedIndex === undefined && props.setCurrentTab(3)) ||
                  (props.selectedIndex > 1 && props.setCurrentTab(props.selectedIndex - 1));
              }}
            >
              Previous
            </button>
          ) : null}
          {props.selectedIndex > -1 ? (
            <button
              type="button"
              className="cy-col-btn btn btn-outline-dark cy-btn"
              onClick={() => {
                props.selectedIndex < 8 && props.setCurrentTab(props.selectedIndex + 1);
              }}
            >
              Next
            </button>
          ) : null}
        </div>
        <div className="footer-layout">
          <button type="button" className="btn btn-outline-dark cy-btn" onClick={() => history.push(`${cancelPath}`)}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary cy-btn"
            onClick={async () => {
              try {
                let geographyData = flatRightTreeData().map((item) => item.id);
                dispatch(showLoader());
                let res = await addSelectedGeographyTags(companyId, { geographyIds: geographyData });
                dispatch(hideLoader());
                dispatch(actionSuccess(SUCCESS_GEO));
              } catch (error) {
                dispatch(hideLoader());
                dispatch(actionError("Something Went Wrong...!"));
              }
            }}
          >
            Save
          </button>
        </div>
      </div>
    </>
  );
};

export default GeographyTagging;
